/**
 * Probar lead — diagnóstico origen + campaña (espejo Kommo, mismas reglas gastoRetorno).
 * @module-isolated analytics-leads-probe
 */
import { useState } from 'react';
import { fetchLeadProbe } from '../../../services/analytics-api.js';
import { getIdToken } from '../../../services/firebase-auth.js';

function statusLabel(status) {
  if (status === 'meta') return 'Meta';
  if (status === 'other') return 'No Meta (alianza)';
  if (status === 'unclassified') return 'Sin clasificar';
  if (status === 'none') return 'Sin etiqueta';
  return status || '—';
}

function sourceLabel(src) {
  if (src === 'kommo') return 'Campos Meta en Kommo';
  if (src === 'config_map') return 'Mapa Config → Orígenes';
  return 'Sin campaña atribuida';
}

export default function LeadProbePanel() {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  async function runProbe(ev) {
    if (ev && ev.preventDefault) ev.preventDefault();
    const query = String(q || '').trim();
    if (!query) {
      setError('Indicá ID Kommo, teléfono, nombre, etiqueta o Contact ID.');
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const token = await getIdToken(false);
      const res = await fetchLeadProbe(query, token);
      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'No se pudo consultar el lead.');
      }
      setResult(res);
    } catch (err) {
      setError((err && err.message) || 'Error al probar lead.');
    } finally {
      setBusy(false);
    }
  }

  const hits = (result && Array.isArray(result.hits) && result.hits) || [];

  return (
    <div className="analytics-lead-probe">
      <form className="analytics-lead-probe-form" onSubmit={runProbe}>
        <input
          type="search"
          className="analytics-input analytics-lead-probe-input"
          placeholder="ID Kommo, teléfono, nombre, etiqueta (DeepSales / fb…) o Contact ID"
          value={q}
          disabled={busy}
          onChange={(ev) => setQ(ev.target.value)}
          aria-label="Buscar lead por ID, teléfono, nombre o etiqueta"
        />
        <button type="submit" className="analytics-btn" disabled={busy}>
          {busy ? 'Buscando…' : 'Probar'}
        </button>
      </form>
      {error ? <p className="analytics-error-inline">{error}</p> : null}
      {result && !hits.length ? (
        <p className="analytics-empty">
          Sin coincidencias en el espejo para «{result.query}» (escaneados {result.scanned || 0}
          ). Si es un Lead ID reciente y no aparece, el espejo puede estar truncado (~2000). Tras
          este deploy se consulta Kommo en vivo por ID — recargá la página.
        </p>
      ) : null}
      {hits.map((h) => (
        <article key={h.leadId || h.name} className="analytics-panel analytics-lead-probe-card">
          <header className="analytics-lead-probe-head">
            <strong>{h.name || '—'}</strong>
            <span className="analytics-sub">#{h.leadId}</span>
            {h.dataSource === 'kommo_live' ? (
              <span className="analytics-chip">Kommo en vivo</span>
            ) : (
              <span className="analytics-chip is-live">Espejo</span>
            )}
            {h.createdAt ? <span className="analytics-sub">Creado {h.createdAt}</span> : null}
            {h.primaryPhone ? (
              <span className="analytics-sub">Tel. {h.primaryPhone}</span>
            ) : null}
          </header>
          <p className="analytics-section-note" style={{ marginTop: 6 }}>
            {h.pipelineName || '—'} → {h.stageName || '—'}
            {h.contactId ? ' · Contact ' + h.contactId : ''}
          </p>
          <dl className="analytics-lead-probe-dl">
            <div>
              <dt>Origen</dt>
              <dd>
                <span
                  className={
                    'analytics-origin-badge ' +
                    (h.origin && h.origin.status === 'meta'
                      ? 'is-meta'
                      : h.origin && h.origin.status === 'other'
                        ? 'is-other'
                        : 'is-muted')
                  }
                >
                  {statusLabel(h.origin && h.origin.status)}
                </span>
                {h.origin && h.origin.tagName ? ' · ' + h.origin.tagName : ''}
              </dd>
            </div>
            <div>
              <dt>Campaña Ads</dt>
              <dd>
                {h.campaign && (h.campaign.name || h.campaign.id) ? (
                  <>
                    <strong>{h.campaign.name || h.campaign.id}</strong>
                    {h.campaign.id ? (
                      <span className="analytics-sub"> ID {h.campaign.id}</span>
                    ) : null}
                    <span className="analytics-sub">
                      {' '}
                      · {sourceLabel(h.campaign.source)}
                      {h.campaign.fromTag ? ' («' + h.campaign.fromTag + '»)' : ''}
                    </span>
                  </>
                ) : (
                  <span className="analytics-sub">{sourceLabel('none')}</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Etiquetas</dt>
              <dd>
                {(h.tagConfigs || []).length
                  ? (h.tagConfigs || []).map((t) => (
                      <span key={t.tagName} className="analytics-lead-probe-tag">
                        <strong>{t.tagName}</strong>
                        {!t.inConfig
                          ? ' (no está en Config)'
                          : t.isMeta === true
                            ? ' · Meta' +
                              (t.metaCampaignId || t.metaCampaignName
                                ? ' → ' + (t.metaCampaignName || t.metaCampaignId)
                                : ' sin mapa campaña')
                            : t.isMeta === false
                              ? ' · No Meta'
                              : ' · sin clasificar'}
                      </span>
                    ))
                  : '—'}
              </dd>
            </div>
          </dl>
          {h.explanation ? (
            <p className="analytics-section-note" style={{ marginTop: 8 }}>
              {h.explanation}
            </p>
          ) : null}
        </article>
      ))}
      {result && result.note ? (
        <p className="analytics-section-note" style={{ marginTop: 8 }}>
          {result.note}
        </p>
      ) : null}
    </div>
  );
}
